/**
 * AI Settings Store
 * Manages persistence and migration of AI settings
 */

import { AISettings } from '@/types/ai-settings';
import { SecureAPIKeyManager } from '../security/api-key-manager';

export interface AISettingsStore {
  load(): Promise<AISettings>;
  save(settings: AISettings): Promise<void>;
  migrate(oldVersion: string): Promise<void>;
  export(): Promise<string>;
  import(data: string): Promise<void>;
  reset(): Promise<void>;
}

const DEFAULT_SETTINGS: AISettings = {
  provider: 'openai',
  model: 'gpt-4-turbo-preview',
  apiKeys: {},
  temperature: 0.7,
  maxTokens: 4096,
  streaming: true,
  advanced: {
    timeout: 30000,
    retries: 3,
    cache: true,
    responseFormat: 'text',
  },
  mcp: {
    enabled: false,
    transport: 'http',
  },
};

export class LocalStorageAISettingsStore implements AISettingsStore {
  private storageKey = 'vibecode-ai-settings';
  private versionKey = 'vibecode-ai-settings-version';
  private currentVersion = '1.0.0';
  private keyManager: SecureAPIKeyManager;

  constructor(keyManager?: SecureAPIKeyManager) {
    this.keyManager = keyManager || new SecureAPIKeyManager();
  }

  async load(): Promise<AISettings> {
    if (typeof window === 'undefined') {
      return DEFAULT_SETTINGS;
    }

    try {
      const stored = localStorage.getItem(this.storageKey);
      if (!stored) {
        return DEFAULT_SETTINGS;
      }

      const settings = JSON.parse(stored) as AISettings;
      
      // Check version and migrate if needed
      const version = localStorage.getItem(this.versionKey);
      if (version !== this.currentVersion) {
        await this.migrate(version || '0.0.0');
      }

      // Load API keys from secure storage
      const providers = await this.keyManager.listProviders();
      const apiKeys: Record<string, string> = {};
      
      for (const provider of providers) {
        const key = await this.keyManager.retrieve(provider);
        if (key) {
          apiKeys[provider] = key;
        }
      }

      return {
        ...DEFAULT_SETTINGS,
        ...settings,
        apiKeys,
      };
    } catch (error) {
      console.error('Failed to load AI settings:', error);
      return DEFAULT_SETTINGS;
    }
  }

  async save(settings: AISettings): Promise<void> {
    if (typeof window === 'undefined') {
      return;
    }

    try {
      // Save API keys to secure storage
      for (const [provider, key] of Object.entries(settings.apiKeys)) {
        if (key) {
          await this.keyManager.store(provider, key);
        } else {
          await this.keyManager.remove(provider);
        }
      }

      // Don't store API keys in regular settings
      const { apiKeys, ...settingsWithoutKeys } = settings;

      localStorage.setItem(this.storageKey, JSON.stringify(settingsWithoutKeys));
      localStorage.setItem(this.versionKey, this.currentVersion);
    } catch (error) {
      console.error('Failed to save AI settings:', error);
      throw error;
    }
  }

  async migrate(oldVersion: string): Promise<void> {
    console.log(`Migrating AI settings from version ${oldVersion} to ${this.currentVersion}`);

    // Migration logic for different versions
    if (oldVersion < '1.0.0') {
      // Initial migration
      const oldSettings = localStorage.getItem('ai-settings');
      if (oldSettings) {
        try {
          const parsed = JSON.parse(oldSettings);
          await this.save({
            ...DEFAULT_SETTINGS,
            ...parsed,
          });
          localStorage.removeItem('ai-settings');
        } catch (error) {
          console.error('Migration failed:', error);
        }
      }
    }

    // Future migrations would go here
  }

  async export(): Promise<string> {
    const settings = await this.load();
    
    // Create export data with metadata
    const exportData = {
      version: this.currentVersion,
      timestamp: new Date().toISOString(),
      settings: {
        ...settings,
        // Mask API keys for security
        apiKeys: Object.keys(settings.apiKeys).reduce((acc, key) => {
          acc[key] = '***MASKED***';
          return acc;
        }, {} as Record<string, string>),
      },
    };

    return JSON.stringify(exportData, null, 2);
  }

  async import(data: string): Promise<void> {
    try {
      const parsed = JSON.parse(data);
      
      if (!parsed.version || !parsed.settings) {
        throw new Error('Invalid import data format');
      }

      // Don't import API keys for security
      const { apiKeys, ...settingsWithoutKeys } = parsed.settings;

      await this.save({
        ...DEFAULT_SETTINGS,
        ...settingsWithoutKeys,
        apiKeys: {}, // Keep existing keys
      });
    } catch (error) {
      console.error('Failed to import AI settings:', error);
      throw new Error('Failed to import settings: Invalid format');
    }
  }

  async reset(): Promise<void> {
    if (typeof window === 'undefined') {
      return;
    }

    // Clear all API keys
    const providers = await this.keyManager.listProviders();
    for (const provider of providers) {
      await this.keyManager.remove(provider);
    }

    // Reset settings
    localStorage.removeItem(this.storageKey);
    localStorage.removeItem(this.versionKey);
  }
}